import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowUpRight, CheckCircle2, History, Loader2, Lock, LockOpen, Share2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const CARD = "rounded-2xl border border-border/70 bg-card shadow-sm";

function Row({ c, projectId }) {
  const tone = c.ok ? "text-emerald-600 dark:text-emerald-400" : c.severity === "BLOCK" ? "text-rose-600 dark:text-rose-400" : "text-amber-600 dark:text-amber-400";
  const Icon = c.ok ? CheckCircle2 : c.severity === "BLOCK" ? XCircle : AlertTriangle;
  return (
    <div className="flex items-start gap-3 px-5 py-3.5" data-testid={`closeout-check-${c.key}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{c.label}</p>
        {!c.ok && c.detail && <p className="mt-0.5 text-xs text-muted-foreground">{c.detail}</p>}
      </div>
      <div className="shrink-0 text-right">
        {c.ok ? (
          <span className="text-xs text-muted-foreground">Clear</span>
        ) : (
          <span className={`text-sm font-semibold tabular-nums ${tone}`}>
            {c.amount !== null && Number(c.amount) > 0 ? inr(c.amount) : c.count > 1 ? `${c.count} open` : "Open"}
          </span>
        )}
        {!c.ok && c.amount !== null && Number(c.amount) > 0 && c.count > 1 && <p className="text-[11px] text-muted-foreground">{c.count} items</p>}
      </div>
      {!c.ok && c.link && (
        <Link to={c.link} className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground" title="Open" data-testid={`closeout-link-${c.key}`}>
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

export default function Closeout() {
  const { can } = usePermissions();
  const { projectId, project, isProjectManager, refresh } = useProjectCtx();
  // close / offer stock: the project manager, and the org role may add to Close-out (POST under /closeout)
  const canCloseOut = isProjectManager && can("PROJECT_CLOSEOUT", "create");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/projects/${projectId}/closeout`);
      setData(d);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const act = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(""); }
  };

  const closeProject = () => act("close", async () => {
    await api.post(`/projects/${projectId}/closeout/close`, { note });
    toast.success("Project closed");
    setNote("");
    await load();
    if (refresh) refresh();
  });
  const reopen = () => act("reopen", async () => {
    await api.post(`/projects/${projectId}/closeout/reopen`, { reason });
    toast.success("Project reopened");
    setReopenOpen(false); setReason("");
    await load();
    if (refresh) refresh();
  });
  const offerStock = () => act("offer", async () => {
    const { data: r } = await api.post(`/projects/${projectId}/closeout/offer-stock`);
    toast.success(r.offered ? `${r.offered} item(s) offered to other sites` : "Everything is already offered");
    await load();
  });

  if (loading && !data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!data) return null;

  const closed = data.status === "CLOSED";
  const block = data.checks.filter((c) => c.severity === "BLOCK");
  const warn = data.checks.filter((c) => c.severity === "WARN");
  const lastClose = data.history.find((h) => h.action === "CLOSE");
  const canReopen = can("PROJECT_CLOSEOUT", "approve") && can("PROJECT_CLOSEOUT", "create");
  const needNote = data.warnings > 0;

  return (
    <div className="space-y-6" data-testid="closeout-page">
      <PageHeader
        title="Project Close-out"
        description="Clear the checklist, hand leftover stock to other sites, then close. A closed project accepts no new orders, issues or progress; payments and receipts still go through."
      />

      <div className={`${CARD} flex flex-wrap items-center gap-4 p-5`} data-testid="closeout-banner">
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${closed ? "bg-slate-500/10 text-slate-600 dark:text-slate-300" : data.blocking ? "bg-rose-500/10 text-rose-600" : "bg-emerald-500/10 text-emerald-600"}`}>
          {closed ? <Lock className="h-5 w-5" /> : <LockOpen className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-foreground">
            {closed ? "This project is closed" : data.blocking ? `${data.blocking} item${data.blocking > 1 ? "s" : ""} must be cleared before closing` : "Ready to close"}
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {closed
              ? `Closed${data.actual_end_date ? ` on ${data.actual_end_date.split("-").reverse().join("-")}` : ""}${lastClose?.by ? ` by ${lastClose.by}` : ""}.`
              : data.warnings ? `${data.warnings} more to review — you can close with a written reason.` : "Nothing is open."}
          </p>
        </div>
        {closed && canReopen && (
          <Button variant="outline" onClick={() => setReopenOpen(true)} data-testid="closeout-reopen-button"><LockOpen className="h-4 w-4" /> Reopen</Button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className={CARD}>
          <h2 className="border-b border-border/70 px-5 py-3.5 text-sm font-semibold text-foreground">Must be cleared</h2>
          <div className="divide-y divide-border/60">{block.map((c) => <Row key={c.key} c={c} projectId={projectId} />)}</div>
        </section>
        <section className={CARD}>
          <h2 className="border-b border-border/70 px-5 py-3.5 text-sm font-semibold text-foreground">Review before closing</h2>
          <div className="divide-y divide-border/60">{warn.map((c) => <Row key={c.key} c={c} projectId={projectId} />)}</div>
        </section>
      </div>

      {data.stock.length > 0 && (
        <section className={CARD} data-testid="closeout-stock">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 px-5 py-3.5">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Leftover site stock</h2>
              <p className="text-xs text-muted-foreground">Offer it to other sites, or return it to the vendor / write it off through Site Inventory.</p>
            </div>
            {!closed && canCloseOut && (
              <Button size="sm" onClick={offerStock} disabled={busy === "offer"} data-testid="closeout-offer-stock">
                {busy === "offer" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Offer all to other sites
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">In store</TableHead><TableHead className="text-right">Value</TableHead><TableHead className="text-right">Offered</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.stock.map((r) => (
                  <TableRow key={`${r.warehouse_id}-${r.item_id}`}>
                    <TableCell className="text-sm"><span className="font-mono text-xs text-muted-foreground">{r.item_code}</span> {r.item_name}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.qty}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(r.value)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.offered}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      {!closed && (
        <section className={`${CARD} space-y-3 p-5`} data-testid="closeout-action">
          <h2 className="text-sm font-semibold text-foreground">Close the project</h2>
          {needNote && (
            <div>
              <Label>Why are you closing with these items still open?</Label>
              <Textarea className="mt-1.5" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Retention to be released after the 12-month defects period; balance stock agreed with the client." data-testid="closeout-note" />
            </div>
          )}
          <div className="flex items-center gap-3">
            <Button onClick={closeProject} disabled={!canCloseOut || data.blocking > 0 || busy === "close" || (needNote && note.trim().length < 3)} data-testid="closeout-close-button">
              {busy === "close" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Close project
            </Button>
            {!isProjectManager && <span className="text-xs text-muted-foreground">Only a project manager can close the project.</span>}
          </div>
        </section>
      )}

      {data.history.length > 0 && (
        <section className={CARD} data-testid="closeout-history">
          <h2 className="flex items-center gap-2 border-b border-border/70 px-5 py-3.5 text-sm font-semibold text-foreground"><History className="h-4 w-4" /> History</h2>
          <div className="divide-y divide-border/60">
            {data.history.map((h) => (
              <div key={h.id} className="px-5 py-3.5 text-sm">
                <p className="font-medium text-foreground">{h.action === "CLOSE" ? "Closed" : "Reopened"} <span className="font-normal text-muted-foreground">by {h.by || "—"} · {when(h.at)}</span></p>
                {h.note && <p className="mt-1 text-muted-foreground">{h.note}</p>}
                {h.action === "CLOSE" && h.snapshot && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    At close: contract {inr(h.snapshot.contract_value)} · billed {inr(h.snapshot.billed_gross)} · actual cost {inr(h.snapshot.actual_cost)} · margin {inr(h.snapshot.gross_margin)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Reopen project</DialogTitle></DialogHeader>
          <div>
            <Label>Reason</Label>
            <Textarea className="mt-1.5" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="closeout-reopen-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)}>Cancel</Button>
            <Button onClick={reopen} disabled={reason.trim().length < 3 || busy === "reopen"} data-testid="closeout-reopen-confirm">
              {busy === "reopen" && <Loader2 className="h-4 w-4 animate-spin" />} Reopen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
